import React, { useState } from 'react';
import { useSprings, animated, to as interpolate } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  IconButton
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Event as CalendarIcon,
  LocationOn as LocationIcon,
  Add as PlusIcon
} from '@mui/icons-material';

// Helper function to get category image
const getCategoryImage = (category) => {
  const categoryImages = {
    'Cultural & Historical': 'https://images.unsplash.com/photo-1553835973-dec43bfddbeb?w=800',
    'Arts & Entertainment': 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=800',
    'Culinary & Gastronomy': 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800',
    'Outdoor & Adventure': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
    'Nature & Wildlife': 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=800',
    'default': 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800'
  };
  return categoryImages[category] || categoryImages['default'];
};

const getProfileImage = (index) => {
  const profileImages = [
    'https://images.unsplash.com/photo-1553835973-dec43bfddbeb?w=80',
    'https://images.unsplash.com/photo-1554463529-e27854014799?w=80',
    'https://images.unsplash.com/photo-1521058001910-55b77aba2203?w=80',
    'https://images.unsplash.com/photo-1562307534-a03738d2a81a?w=80'
  ];
  return profileImages[index % profileImages.length];
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const to = (i) => ({
  x: 0,
  y: i * -2,
  scale: 1 - i * 0.05,
  rot: -3 + Math.random() * 6,
  delay: i * 100,
});

const from = (_i) => ({ x: 0, rot: 0, scale: 1.2, y: -500 });

const trans = (r, s) =>
  `rotateZ(${r}deg) scale(${s})`;

const SwipeableCardStack = ({ events = [], onEventClick }) => {
  const [gone] = useState(() => new Set());
  const [props, api] = useSprings(events.length, i => ({
    ...to(i),
    from: from(i),
  }));

  const bind = useDrag(({ args: [index], active, movement: [mx], direction: [xDir], velocity: [vx] }) => {
    const trigger = vx > 0.2;
    if (!active && trigger) gone.add(index);

    api.start(i => {
      if (index !== i) return;
      const isGone = gone.has(index);
      const x = isGone ? (200 + window.innerWidth) * xDir : active ? mx : 0;
      const rot = mx / 100 + (isGone ? xDir * 10 * vx : 0);
      const scale = active ? 1.1 : 1;
      return {
        x,
        rot,
        scale,
        delay: undefined,
        config: { friction: 50, tension: active ? 800 : isGone ? 200 : 500 },
      };
    });

    if (!active && gone.size === events.length) {
      setTimeout(() => {
        gone.clear();
        api.start(i => to(i));
      }, 600);
    }
  });

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '90%', sm: '400px', md: '450px' },
        height: { xs: '400px', sm: '500px', md: '550px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        touchAction: 'none',
        margin: '0 auto'
      }}
    >
      {props.map(({ x, y, rot, scale }, i) => (
        <animated.div
          key={i}
          style={{
            position: 'absolute',
            width: '85%',
            height: '85%',
            willChange: 'transform',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            transform: interpolate([x, y], (x, y) => `translate3d(${x}px,${y}px,0)`),
          }}
        >
          <animated.div
            {...bind(i)}
            style={{
              width: '100%',
              height: '100%',
              transform: interpolate([rot, scale], trans),
              touchAction: 'none',
              cursor: 'grab',
            }}
          >
            <Card
              onClick={() => onEventClick && onEventClick(events[i])}
              sx={{
                width: '100%',
                height: '100%',
                borderRadius: '16px',
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                transition: 'box-shadow 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                },
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%), url(${getCategoryImage(events[i].category)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                touchAction: 'none'
              }}
            >
              {/* Profile Image */}
              <Avatar
                src={getProfileImage(i)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  width: 50,
                  height: 50,
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              />

              {/* Add Button */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Add event:', events[i]);
                }}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: '#00cc99',
                  color: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    backgroundColor: '#00a880'
                  }
                }}
              >
                <PlusIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <CardContent sx={{
                p: 2.5,
                color: '#fff',
                position: 'relative',
                zIndex: 1,
                '&:last-child': { pb: 2.5 }
              }}>
                {/* Event Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    fontSize: '1.25rem',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    lineHeight: 1.3
                  }}
                >
                  {events[i].title}
                </Typography>

                {/* Event Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                  {/* Duration */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClockIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.9rem', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {events[i].duration || '4 hours'}
                    </Typography>
                  </Box>

                  {/* Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.9rem', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {formatDate(events[i].start)}
                    </Typography>
                  </Box>

                  {/* Location */}
                  {events[i].location && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.9rem', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                        {events[i].location}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Category Tag */}
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.4,
                    backgroundColor: 'rgba(0, 204, 153, 0.9)',
                    borderRadius: 1.5,
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  {events[i].category}
                </Box>
              </CardContent>
            </Card>
          </animated.div>
        </animated.div>
      ))}
    </Box>
  );
};

export default SwipeableCardStack;
